import React, { useMemo, useState } from 'react';
import { PiArrowRight, PiCheckCircle } from 'react-icons/pi';
import { COMPANY } from '../config/company';

const FORM_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;

export const ENQUIRY_OPTIONS = [
  { value: 'support', label: 'Product support' },
  { value: 'website', label: 'Website project' },
  { value: 'website-health-check', label: 'Website Health Check' },
  { value: 'general', label: 'General enquiry' },
];

export const getInitialEnquiryType = (fallback = 'support') => {
  const type = new URLSearchParams(window.location.search).get('type');
  if (ENQUIRY_OPTIONS.some((option) => option.value === type)) return type;
  if (ENQUIRY_OPTIONS.some((option) => option.value === fallback)) return fallback;
  return 'support';
};

const ContactForm = ({
  defaultType = 'support',
  heading = 'Send us a message',
  intro = 'Name, email and a short message are required.',
}) => {
  const [enquiryType, setEnquiryType] = useState(() => getInitialEnquiryType(defaultType));
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const isHealthCheck = enquiryType === 'website-health-check';
  const selectedLabel = useMemo(
    () => ENQUIRY_OPTIONS.find((option) => option.value === enquiryType)?.label,
    [enquiryType]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const form = event.currentTarget;
    const data = new FormData(form);

    if (data.get('company_website')) return;

    if (!FORM_ENDPOINT) {
      const websiteUrl = data.get('website_url');
      const subject = `${selectedLabel} — ${data.get('name')}`;
      const body = [
        `Name: ${data.get('name')}`,
        `Email: ${data.get('email')}`,
        `Enquiry type: ${selectedLabel}`,
        websiteUrl ? `Website: ${websiteUrl}` : null,
        '',
        data.get('message'),
      ].filter(Boolean).join('\n');
      window.location.href = `mailto:${COMPANY.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      return;
    }

    setStatus('sending');
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to send your message.');
      form.reset();
      setEnquiryType(defaultType);
      setStatus('sent');
    } catch (submissionError) {
      setStatus('idle');
      setError('We could not send your message. Please email us directly instead.');
    }
  };

  if (status === 'sent') {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center text-center" role="status">
        <PiCheckCircle className="text-6xl text-green" aria-hidden="true" />
        <h2 className="mt-5 text-3xl font-bold">Message sent</h2>
        <p className="mt-3 max-w-sm leading-7 text-ink/65">Thank you for getting in touch. We’ll reply as soon as we can.</p>
        <button type="button" onClick={() => setStatus('idle')} className="btn-navy mt-8">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate={false}>
      <h2 className="text-2xl font-bold tracking-[-0.03em]">{heading}</h2>
      <p className="mt-2 text-sm leading-6 text-ink/55">{intro}</p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <label className="form-field">
          <span>Name</span>
          <input name="name" type="text" autoComplete="name" required className="form-input" placeholder="Your name" />
        </label>
        <label className="form-field">
          <span>Email</span>
          <input name="email" type="email" autoComplete="email" required className="form-input" placeholder="you@example.com" />
        </label>
      </div>

      <fieldset className="mt-7">
        <legend className="text-sm font-semibold text-ink/80">What can we help with?</legend>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {ENQUIRY_OPTIONS.map((option) => (
            <label key={option.value} className="enquiry-option">
              <input
                type="radio"
                name="enquiry_type"
                value={option.value}
                checked={enquiryType === option.value}
                onChange={() => setEnquiryType(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="form-field mt-7">
        <span>{isHealthCheck ? 'Website URL' : 'Website URL (optional)'}</span>
        <input
          name="website_url"
          type="text"
          inputMode="url"
          autoComplete="url"
          required={isHealthCheck}
          className="form-input"
          placeholder="https://yourwebsite.co.uk"
        />
        <span className="font-normal text-ink/50">
          Please share the public website address only. Do not send passwords or admin logins.
        </span>
      </label>

      <label className="form-field mt-7">
        <span>Message</span>
        <textarea name="message" required rows="6" className="form-input resize-y" placeholder="A brief description is perfect." />
      </label>

      <label className="sr-only" aria-hidden="true">
        Company website
        <input name="company_website" type="text" tabIndex="-1" autoComplete="off" />
      </label>

      {error && <p className="mt-5 rounded-lg bg-[#fff0f3] px-4 py-3 text-sm text-[#a5194a]" role="alert">{error}</p>}

      <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-sm text-xs leading-5 text-ink/50">
          We’ll use these details only to respond to your enquiry. Please don’t include passwords or payment-card details.
        </p>
        <button type="submit" disabled={status === 'sending'} className="btn-navy shrink-0 disabled:cursor-wait disabled:opacity-60">
          {status === 'sending' ? 'Sending…' : FORM_ENDPOINT ? 'Send enquiry' : 'Continue in email'}
          {status !== 'sending' && <PiArrowRight aria-hidden="true" />}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
